import type { Ticket, TicketMessage, TicketStatus } from '@sistemas/contracts'

import { describeDbError, getSupabase } from '@/lib/supabase'
import type { ProjectsResult } from '@/modules/projects/projects-api'

/**
 * C4 — chamados, só leitura (ADR-0024): o time atende no Portal de Chamados.
 * A tabela `ticket` não tem policy de escrita, e as RPCs que mudam o chamado
 * só a `service_role` executa.
 */

export interface TicketRow {
  id: string
  source: Ticket['source']
  portal_id: string | null
  number: string
  title: string
  description: string
  requester_name: string
  requester_sector: string
  requester_contact: string | null
  category: string
  priority: string
  status: Ticket['status']
  opened_at: string
  sla_due_at: string | null
  created_by: string | null
  // Colunas da migration 20261011100000 — opcionais para a tela não quebrar
  // enquanto ela não chegou ao banco.
  system_id?: string | null
  system_choice?: Ticket['systemChoice'] | null
  requester_whatsapp?: string | null
  requester_extension?: string | null
  attendant_id?: string | null
  public_code?: string | null
  resolved_at?: string | null
  reopen_until?: string | null
  last_progress_at?: string | null
}

export interface TicketTriageRow {
  ticket_id: string
  triage_status: Ticket['triageStatus']
  resolution: string | null
  answered_at: string | null
  triaged_by: string | null
  updated_at: string | null
}

export function toTicket(
  row: TicketRow,
  triage: TicketTriageRow | undefined,
  activityIds: readonly string[],
): Ticket {
  const triaged = triage && triage.triage_status !== 'pendente'
  return {
    id: row.id,
    source: row.source ?? 'portal',
    number: row.number,
    title: row.title,
    description: row.description,
    requesterName: row.requester_name,
    requesterSector: row.requester_sector,
    requesterContact: row.requester_contact ?? '',
    category: row.category,
    priority: row.priority,
    status: row.status,
    triageStatus: triage?.triage_status ?? 'pendente',
    openedAt: row.opened_at,
    activityIds,
    systemChoice: row.system_choice ?? (row.system_id ? 'sistema' : 'nao_sei'),
    publicCode: row.public_code ?? '',
    lastProgressAt: row.last_progress_at ?? row.opened_at,
    ...(row.portal_id ? { portalId: row.portal_id } : {}),
    ...(row.created_by ? { createdBy: row.created_by } : {}),
    ...(triaged && triage.triaged_by ? { triagedBy: triage.triaged_by } : {}),
    ...(triaged && triage.updated_at ? { triagedAt: triage.updated_at } : {}),
    ...(row.sla_due_at ? { slaDueAt: row.sla_due_at } : {}),
    ...(triage?.resolution ? { resolution: triage.resolution } : {}),
    ...(triage?.answered_at ? { answeredAt: triage.answered_at } : {}),
    ...(row.system_id ? { systemId: row.system_id } : {}),
    ...(row.requester_whatsapp ? { requesterWhatsapp: row.requester_whatsapp } : {}),
    ...(row.requester_extension ? { requesterExtension: row.requester_extension } : {}),
    ...(row.attendant_id ? { attendantId: row.attendant_id } : {}),
    ...(row.resolved_at ? { resolvedAt: row.resolved_at } : {}),
    ...(row.reopen_until ? { reopenUntil: row.reopen_until } : {}),
  }
}

/** Relê os chamados (fila, triagem e vínculos) — depois de uma escrita. */
export async function fetchTickets(): Promise<ProjectsResult<Ticket[]>> {
  const sb = getSupabase()
  const [tickets, triages, links] = await Promise.all([
    sb.from('ticket').select('*').order('opened_at', { ascending: false }).limit(500),
    sb.from('ticket_triage').select('*'),
    sb.from('ticket_activity').select('ticket_id, activity_id'),
  ])
  const failure = tickets.error ?? triages.error ?? links.error
  if (failure) return { ok: false, reason: describeDbError(failure, 'Não foi possível carregar os chamados.') }
  return { ok: true, data: mapTickets(tickets.data ?? [], triages.data ?? [], links.data ?? []) }
}

export function mapTickets(
  tickets: readonly unknown[],
  triages: readonly unknown[],
  links: readonly unknown[],
): Ticket[] {
  const triageBy = new Map((triages as TicketTriageRow[]).map((t) => [t.ticket_id, t]))
  const activitiesBy = new Map<string, string[]>()
  for (const link of links as { ticket_id: string; activity_id: string }[]) {
    const list = activitiesBy.get(link.ticket_id) ?? []
    list.push(link.activity_id)
    activitiesBy.set(link.ticket_id, list)
  }
  return (tickets as TicketRow[]).map((row) =>
    toTicket(row, triageBy.get(row.id), activitiesBy.get(row.id) ?? []),
  )
}

interface TicketMessageRow {
  id: string
  ticket_id: string
  author_kind: TicketMessage['authorKind']
  author_member_id: string | null
  visibility: TicketMessage['visibility']
  body: string
  status_from: TicketStatus | null
  status_to: TicketStatus | null
  created_at: string
}

function toMessage(r: TicketMessageRow): TicketMessage {
  return {
    id: r.id,
    ticketId: r.ticket_id,
    authorKind: r.author_kind,
    visibility: r.visibility,
    body: r.body,
    createdAt: r.created_at,
    ...(r.author_member_id ? { authorMemberId: r.author_member_id } : {}),
    ...(r.status_from ? { statusFrom: r.status_from } : {}),
    ...(r.status_to ? { statusTo: r.status_to } : {}),
  }
}

export async function fetchTicketMessages(ticketId: string): Promise<ProjectsResult<TicketMessage[]>> {
  const { data, error } = await getSupabase()
    .from('ticket_message')
    .select('*')
    .eq('ticket_id', ticketId)
    .order('created_at', { ascending: true })
  if (error) return { ok: false, reason: describeDbError(error, 'Não foi possível ler as mensagens do chamado.') }
  return { ok: true, data: ((data ?? []) as TicketMessageRow[]).map(toMessage) }
}

export interface SystemError {
  readonly id: string
  readonly title: string
  readonly container: string | null
  readonly occurrences: number
  readonly lastSeen: string
  readonly linked: boolean
}

/**
 * Os erros do sistema do chamado nas últimas 24 h, mais os já ligados a ele —
 * "o que o log diz sobre o que a pessoa viu".
 */
export async function fetchTicketSystemErrors(ticketId: string, systemId: string): Promise<ProjectsResult<SystemError[]>> {
  const sb = getSupabase()
  const since = new Date(Date.now() - 24 * 3_600_000).toISOString()
  const [recent, links] = await Promise.all([
    sb.from('error_group')
      .select('id, title, human_title, container, occurrences, last_seen')
      .eq('system_id', systemId)
      .gte('last_seen', since)
      .order('last_seen', { ascending: false })
      .limit(10),
    sb.from('ticket_error_link').select('error_group_id').eq('ticket_id', ticketId),
  ])
  const failure = recent.error ?? links.error
  if (failure) return { ok: false, reason: describeDbError(failure, 'Não foi possível ler os erros do sistema.') }
  const linked = new Set(((links.data ?? []) as { error_group_id: string }[]).map((l) => l.error_group_id))
  return {
    ok: true,
    data: (
      (recent.data ?? []) as {
        id: string
        title: string
        human_title: string | null
        container: string | null
        occurrences: number
        last_seen: string
      }[]
    ).map((e) => ({
      id: e.id,
      title: e.human_title ?? e.title,
      container: e.container,
      occurrences: Number(e.occurrences ?? 0),
      lastSeen: e.last_seen,
      linked: linked.has(e.id),
    })),
  }
}

export interface TicketRef {
  id: string
  number: string
  requesterName: string
}

/**
 * O chamado de onde a atividade veio. Consulta própria porque o quadro do
 * projeto não carrega a fila de chamados (domínio `core`).
 */
export async function fetchTicketOfActivity(activityId: string): Promise<TicketRef | null> {
  const { data, error } = await getSupabase()
    .from('ticket_activity')
    .select('ticket:ticket_id (id, number, requester_name)')
    .eq('activity_id', activityId)
    .limit(1)
    .maybeSingle()
  const ticket = (data as { ticket: { id: string; number: string; requester_name: string } | null } | null)?.ticket
  if (error || !ticket) return null
  return { id: ticket.id, number: ticket.number, requesterName: ticket.requester_name }
}

export interface TicketPhoto {
  readonly id: string
  readonly messageId: string | null
  readonly url: string
  /** Foto aparece em miniatura; PDF, pelo nome. */
  readonly kind: 'imagem' | 'pdf'
  readonly name: string | null
}

/**
 * As fotos que o solicitante mandou pelo link público (bucket privado
 * `chamados`). URL assinada por uma hora — o bastante para a tela aberta.
 * Sem a migration 20261012100000 a tabela não existe: devolve vazio.
 */
export async function fetchTicketPhotos(ticketId: string): Promise<TicketPhoto[]> {
  const supabase = getSupabase()
  const { data, error } = await supabase
    .from('ticket_attachment')
    .select('*')
    .eq('ticket_id', ticketId)
    .order('created_at', { ascending: true })
  const rows = (data ?? []) as {
    id: string
    message_id: string | null
    path: string
    content_type: string
    file_name?: string | null
  }[]
  if (error || rows.length === 0) return []
  const signed = await supabase.storage.from('chamados').createSignedUrls(
    rows.map((r) => r.path),
    3600,
  )
  const byPath = new Map((signed.data ?? []).map((s) => [s.path, s.signedUrl]))
  return rows.flatMap((r) => {
    const url = byPath.get(r.path)
    if (!url) return []
    const kind = r.content_type === 'application/pdf' ? ('pdf' as const) : ('imagem' as const)
    return [{ id: r.id, messageId: r.message_id, url, kind, name: r.file_name ?? null }]
  })
}

let publicBase: Promise<string> | null = null

/**
 * O endereço público do Hub (`app_setting.public_base_url`), o mesmo que o
 * banco põe nas mensagens do WhatsApp — e não a origem da aba, que pode ser
 * o IP da rede interna.
 */
export function fetchPublicBaseUrl(): Promise<string> {
  publicBase ??= Promise.resolve(
    getSupabase().from('app_setting').select('value').eq('key', 'public_base_url').maybeSingle(),
  ).then(({ data }) => ((data as { value: string } | null)?.value ?? window.location.origin).replace(/\/+$/, ''))
  return publicBase
}
